import React, { useCallback, useEffect, useMemo, useState } from "react";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { PlayerDirectory } from "../../../components/player/PlayerAvatar";
import { useIsMobile } from "../../../hooks/isMobile/isMobile";
import { Rules as RulesType } from "../types/rules";
import { isGameStarted, sortGamesByStartStatus } from "../utils/game";
import { getTeamPollRank } from "../utils/poll";
import { getTeamRecord } from "../utils/records";
import { useCfbd } from "../hooks/useCfbd/useCfbd";
import { useLeague } from "../../../hooks/useLeague/useLeague";
import { SelectedGame } from "../types/game";
import { PollsResponse } from "../types/poll";
import { RecordsResponse } from "../types/records";
import { PickedGame, UpdatePicksRequest } from "../types/game";

// Import sub-components
import { PickerHeader } from "./components/PickerHeader";
import { PickerGames } from "./components/PickerGames";
import { PickerPagination } from "./components/PickerPagination";
import { PickerErrorBoundary } from "./components/PickerErrorBoundary";
import PickerViewSwitcher from "./components/PickerViewSwitcher";
import {
  PickerViewMode,
  readPickerViewMode,
  writePickerViewMode,
} from "./viewMode";

// Import utility functions
import {
  calculatePickedCount,
  calculateLockCount,
  canAddLock,
  filterGames,
  calculatePaginationInfo,
} from "./Utils";

// Import UI components
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

interface PickerProps {
  leagueId?: string;
  userId?: string;
  rules?: RulesType;
  userEmail?: string;
  playerDirectory?: PlayerDirectory;
}

const Picker: React.FC<PickerProps> = (props) => {
  const { getPollsAndRecords } = useCfbd();
  const { getSelectedGames, getPicks, getAllPicksForWeek, updatePicks } =
    useLeague();
  const isMobile = useIsMobile();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [games, setGames] = useState<SelectedGame[]>([]);
  const [gameOfTheWeek, setGameOfTheWeek] = useState<number | undefined>(
    undefined
  );
  const [ready, setReady] = useState(false);
  const [picks, setPicks] = useState<Record<number, number>>({});
  const [locks, setLocks] = useState<Record<number, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [visiblePicks, setVisiblePicks] = useState<
    Record<string, PickedGame[]>
  >({});
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pollsData, setPollsData] = useState<PollsResponse | null>(null);
  const [recordsData, setRecordsData] = useState<RecordsResponse | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [showUnpickedOnly, setShowUnpickedOnly] = useState(false);
  const [year, setYear] = useState<number | undefined>(undefined);
  const [week, setWeek] = useState<number | undefined>(undefined);
  // Track if user manually changed rowsPerPage so we don't auto-adjust after
  const [userChangedRowsPerPage, setUserChangedRowsPerPage] = useState(false);
  const [viewMode, setViewMode] = useState<PickerViewMode>(() =>
    readPickerViewMode(
      isCompact,
      typeof window === "undefined" ? undefined : window.localStorage,
    ),
  );

  // Derived state using utility functions
  const totalGames = games.length;
  const pickedCount = useMemo(
    () => calculatePickedCount(games, picks),
    [games, picks]
  );
  const lockCount = useMemo(() => calculateLockCount(locks), [locks]);

  // Rules flags
  const maxLocks = props.rules?.lockRules?.count || 0;
  const showLocks = props.rules?.lockRules !== undefined;
  const showGameOfTheWeek = props.rules?.gameOfTheWeekRules !== undefined;

  // Helper to choose the smallest page size option that can display all items (round up)
  const computeOptimalPageSize = (count: number, options: number[]) => {
    for (const o of options) if (count <= o) return o;
    return options[options.length - 1];
  };

  // Initialize / adapt rowsPerPage based on total games when device breakpoint changes
  useEffect(() => {
    if (userChangedRowsPerPage) return; // respect manual selection
    const options = isCompact ? [12, 24] : [24, 48, 96];
    const optimal = computeOptimalPageSize(games.length, options);
    if (optimal !== rowsPerPage) {
      setRowsPerPage(optimal);
      setPage(0);
    }
  }, [isCompact, games.length, userChangedRowsPerPage, rowsPerPage]);

  useEffect(() => {
    setViewMode(
      readPickerViewMode(
        isCompact,
        typeof window === "undefined" ? undefined : window.localStorage,
      ),
    );
  }, [isCompact]);

  const handleViewModeChange = (nextMode: PickerViewMode) => {
    setViewMode(nextMode);
    writePickerViewMode(
      isCompact,
      nextMode,
      typeof window === "undefined" ? undefined : window.localStorage,
    );
  };
  const refreshVisiblePicks = useCallback(async () => {
    if (!props.leagueId || !year || !week) return;
    const response = await getAllPicksForWeek(props.leagueId, year, week);
    if (!(response instanceof Error)) {
      setVisiblePicks(response.picks || {});
    }
  }, [getAllPicksForWeek, props.leagueId, week, year]);


  // Data fetching effect
  useEffect(() => {
    const fetchData = async () => {
      if (!props.leagueId) return;

      setLoading(true);
      setError(null);

      try {
        // Fetch games
        const gamesData = await getSelectedGames(
          props.leagueId,
          year ?? 0,
          week ?? 0
        );
        if (gamesData instanceof Error) {
          setError(`Error fetching selected games: ${gamesData.message}`);
          return;
        }

        setGames(gamesData.games);
        setGameOfTheWeek(gamesData.gameOfTheWeek);
        setReady(gamesData.ready);
        setYear(gamesData.year);
        setWeek(gamesData.week);

        // Fetch picks
        const picksData = await getPicks(props.leagueId);
        if (picksData instanceof Error) {
          setError(`Error fetching picks: ${picksData.message}`);
          return;
        }

        const picksMap: Record<number, number> = {};
        const locksMap: Record<number, boolean> = {};
        for (const pick of picksData.picks) {
          picksMap[pick.gameId] = pick.pick;
          locksMap[pick.gameId] = pick.lock;
        }
        setPicks(picksMap);
        setLocks(locksMap);

        // Fetch polls and records
        if (gamesData.year && gamesData.week) {
          const allTeamIds = Array.from(
            new Set([
              ...gamesData.games.map((g: SelectedGame) => g.homeTeam.id),
              ...gamesData.games.map((g: SelectedGame) => g.awayTeam.id),
            ])
          );
          if (allTeamIds.length > 0) {
            const pollsAndRecordsResponse = await getPollsAndRecords(
              gamesData.year,
              gamesData.week,
              allTeamIds
            );
            if (pollsAndRecordsResponse instanceof Error) {
              setError(
                `Error fetching polls: ${pollsAndRecordsResponse.message}`
              );
              return;
            }
            setPollsData(pollsAndRecordsResponse.polls);
            setRecordsData(pollsAndRecordsResponse.records);
          }
        }
      } catch (err: any) {
        setError(`Unexpected error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    props.leagueId,
    getPollsAndRecords,
    getSelectedGames,
    getPicks,
    year,
    week,
  ]);
  useEffect(() => {
    refreshVisiblePicks();
  }, [refreshVisiblePicks]);

  useEffect(() => {
    const now = Date.now();
    const nextKickoff = games
      .map((game) => Date.parse(game.game.time))
      .filter((kickoff) => Number.isFinite(kickoff) && kickoff > now)
      .sort((a, b) => a - b)[0];
    if (!nextKickoff) return;
    const timer = window.setTimeout(
      refreshVisiblePicks,
      Math.max(0, nextKickoff - now + 1000),
    );
    return () => window.clearTimeout(timer);
  }, [games, refreshVisiblePicks]);
  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") {
        refreshVisiblePicks();
      }
    };
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () =>
      document.removeEventListener("visibilitychange", refreshWhenVisible);
  }, [refreshVisiblePicks]);


  // Note: rows per page is managed by adaptive logic above; we intentionally
  // avoid overriding it on mobile breakpoint changes if the user has manually set it.

  // Event handlers
  const handlePick = (gameId: number, teamId: number) => {
    const gameObj = games.find((g) => Number(g.game.id) === gameId);
    if (gameObj && isGameStarted(gameObj.game.time)) return;

    const gidNum = Number(gameId);
    setPicks((prev) => {
      if (prev[gidNum] === teamId) {
        const { [gidNum]: _, ...rest } = prev;
        setLocks((prevLocks) => {
          const { [gidNum]: _, ...restLocks } = prevLocks;
          return restLocks;
        });
        return rest;
      } else {
        return { ...prev, [gidNum]: teamId };
      }
    });
  };

  const handleToggleLock = (gameId: number) => {
    const gameObj = games.find((g) => Number(g.game.id) === gameId);
    if (gameObj && isGameStarted(gameObj.game.time)) return;

    const gidNum = Number(gameId);
    setLocks((prev) => {
      if (prev[gidNum]) {
        const { [gidNum]: _, ...rest } = prev;
        return rest;
      } else {
        if (!canAddLock(prev, maxLocks)) return prev;
        return { ...prev, [gidNum]: true };
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(null);
    setSaveError(null);

    const picksList: PickedGame[] = Object.entries(picks).map(
      ([gameId, teamId]) => ({
        gameId: Number(gameId),
        pick: teamId as number,
        lock: locks[Number(gameId)] || false,
      })
    );

    if (!props.leagueId) {
      setSaveError("League ID is required");
      setSaving(false);
      return;
    }

    const req: UpdatePicksRequest = { picks: picksList };
    const response = await updatePicks(props.leagueId, req);

    if (response instanceof Error) {
      setSaveError(`Error saving picks: ${response.message}`);
    } else {
      setSaveSuccess("Picks saved!");
      setTimeout(() => setSaveSuccess(null), 2000);
    }

    setSaving(false);
  };

  const handleToggleUnpicked = (checked: boolean) => {
    setShowUnpickedOnly(checked);
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setUserChangedRowsPerPage(true);
  };

  // Filter and sort games
  const filteredGames = filterGames(games, picks, showUnpickedOnly);
  const sortedGames =
    sortGamesByStartStatus(
      filteredGames,
      (game) => game.game.time,
      (game) => game.game.completed ?? false
    ) || [];

  // Pagination
  const { needsPagination, startIndex, endIndex } = calculatePaginationInfo(
    sortedGames.length,
    rowsPerPage,
    page
  );
  const pagedGames = needsPagination
    ? sortedGames.slice(startIndex, endIndex)
    : sortedGames;

  // Loading state
  if (loading) return <CircularProgress sx={{ m: { xs: 2, md: 4 } }} />;

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ m: { xs: 2, md: 4 } }}>
        {error}
      </Alert>
    );
  }

  // Draft state
  if (ready === false) {
    return (
      <Alert severity="info" sx={{ m: { xs: 2, md: 4 } }}>
        This week is still in draft. The league admin must publish the
        selections before picks open.
      </Alert>
    );
  }

  // No games state
  if (!games.length) {
    return (
      <Alert severity="info" sx={{ m: { xs: 2, md: 4 } }}>
        No games to pick from.
      </Alert>
    );
  }

  // Main render
  return (
    <PickerErrorBoundary>
      <Box
        sx={{
          maxWidth: 1080,
          mx: "auto",
          px: { xs: 2, sm: 3 },
          py: { xs: 3, md: 4 },
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "flex-start" }}
          gap={2}
          mb={3}
        >
          <Box>
            <Typography component="h1" variant="h4" fontWeight={850}>
              My Picks
            </Typography>
            <Typography color="text.secondary" mt={0.5}>
              Choose one winner for every matchup before kickoff.
            </Typography>
          </Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={{ xs: "space-between", sm: "flex-end" }}
            gap={0.5}
          >
            <IconButton
              aria-label="Previous week"
              onClick={() =>
                setWeek((value) => Math.max(1, (value ?? 1) - 1))
              }
              disabled={(week ?? 1) <= 1}
            >
              <ChevronLeftRoundedIcon />
            </IconButton>
            <Box sx={{ px: 2, textAlign: "center", minWidth: 120 }}>
              <Typography variant="caption" color="text.secondary">
                {year ?? "Season"}
              </Typography>
              <Typography fontWeight={800}>Week {week ?? "—"}</Typography>
            </Box>
            <IconButton
              aria-label="Next week"
              onClick={() => setWeek((value) => (value ?? 0) + 1)}
            >
              <ChevronRightRoundedIcon />
            </IconButton>
          </Stack>
        </Stack>

        <Card
          variant="outlined"
          sx={{
            p: { xs: 2, sm: 2.5 },
            mb: 2.5,
            bgcolor: "surface.container",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            gap={2}
          >
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" justifyContent="space-between" mb={1}>
                <Typography fontWeight={800}>
                  {pickedCount === totalGames
                    ? "Your card is complete"
                    : "Finish this week’s card"}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={800}
                  color="primary.main"
                >
                  {pickedCount}/{totalGames}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={totalGames ? (pickedCount / totalGames) * 100 : 0}
                aria-label={`${pickedCount} of ${totalGames} picks complete`}
                sx={{ height: 8, borderRadius: 99 }}
              />
            </Box>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              sx={{ minHeight: 44, minWidth: 126 }}
            >
              {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
              {saving ? "Saving" : "Save picks"}
            </Button>
          </Stack>
        </Card>

        <Card
          variant="outlined"
          sx={{ p: 2, mb: 3, bgcolor: "surface.lowest" }}
        >
          <Typography fontWeight={800} mb={0.5}>
            How to play this week
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tap a team to make your pick. Add a lock where available for bonus
            points. Other players’ selections stay hidden until each game kicks
            off.
          </Typography>
        </Card>

        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {saveSuccess}
          </Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          gap={1}
          mb={3}
        >
          <PickerHeader
            pickedCount={pickedCount}
            totalGames={totalGames}
            lockCount={lockCount}
            maxLocks={maxLocks}
            showLocks={showLocks}
            showGameOfTheWeek={showGameOfTheWeek}
            gameOfTheWeek={gameOfTheWeek}
            games={games}
            isMobile={isMobile}
          />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={{ xs: "space-between", sm: "flex-end" }}
            gap={1}
            flexWrap="wrap"
          >
            <FormControlLabel
              control={
                <Switch
                  checked={showUnpickedOnly}
                  onChange={(_, checked) => handleToggleUnpicked(checked)}
                  color="primary"
                />
              }
              label="Unpicked only"
              sx={{ m: 0 }}
            />
            <PickerViewSwitcher
              value={viewMode}
              onChange={handleViewModeChange}
              compact={isCompact}
            />
          </Stack>
        </Stack>

        <PickerGames
          games={viewMode === "wheel" ? sortedGames : pagedGames}
          picks={picks}
          locks={locks}
          onPick={handlePick}
          onToggleLock={handleToggleLock}
          lockCount={lockCount}
          maxLocks={maxLocks}
          showLocks={showLocks}
          showGameOfTheWeek={showGameOfTheWeek}
          gameOfTheWeek={gameOfTheWeek}
          pollsData={pollsData}
          recordsData={recordsData}
          rules={props.rules}
          year={year}
          week={week}
          leagueId={props.leagueId}
          isGameStarted={isGameStarted}
          getTeamPollRank={getTeamPollRank}
          getTeamRecord={getTeamRecord}
          userEmail={props.userEmail}
          viewMode={viewMode}
          compact={isCompact}
          pickedCount={pickedCount}
          totalGames={totalGames}
          visiblePicks={visiblePicks}
          playerDirectory={props.playerDirectory}
          onExitWheel={() => handleViewModeChange("overview")}
        />

        {viewMode === "overview" && (
          <PickerPagination
            needsPagination={needsPagination}
            totalItems={sortedGames.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            isMobile={isMobile}
          />
        )}
      </Box>
    </PickerErrorBoundary>
  );
};

export default Picker;
